// A matemática de reordenar os passos da trilha. Funções puras: recebem dados,
// devolvem dados, nunca tocam no DOM.
//
// Vive separada do componente de propósito. O arrasto é a parte que dá errado em
// silêncio — foi assim que se descobriu que o `react-beautiful-dnd` erra o índice
// numa grade que quebra linha (medido: 1 acerto em 8 arrastos, porque o modelo
// dele tem `line` e `crossAxisLine` e o cálculo do destino ignora o eixo
// transversal). Com a matemática aqui, ela é testada sem montar nada, e o
// componente fica só com o gesto.

/**
 * Move um item de uma posição para outra, empurrando o resto.
 *
 * É o `splice` duplo de sempre, mas com os limites tratados: índice fora da
 * lista devolve a lista intacta em vez de criar buraco ou `undefined` no meio —
 * o que aconteceria com um `splice(-1)` vindo de um arrasto solto fora da área.
 *
 * @param {Array} lista
 * @param {number} de índice de origem
 * @param {number} para índice de destino
 * @returns {Array} lista NOVA (a original não é tocada)
 */
export function mover(lista, de, para) {
  if (!Array.isArray(lista)) return [];
  if (!ehIndiceValido(lista, de) || !ehIndiceValido(lista, para)) return lista;
  if (de === para) return lista;

  const resultado = Array.from(lista);
  const [item] = resultado.splice(de, 1);
  resultado.splice(para, 0, item);
  return resultado;
}

/**
 * O índice do card cujo CENTRO está mais perto do ponto (x, y).
 *
 * É o hit-test do arrasto, e é o que uma lib de eixo único não faz: aqui a
 * distância é euclidiana, então uma grade que quebra linha funciona igual a uma
 * fila — o card da linha de baixo compete com o da linha de cima em pé de
 * igualdade.
 *
 * Recebe retângulos já medidos (`getBoundingClientRect`), e não elementos: é o
 * que mantém a função pura e testável, e também o que evita medir o DOM a cada
 * `pointermove` — quem chama mede uma vez, no início do gesto.
 *
 * @param {Array<{left, top, width, height}>} retangulos na ordem da lista
 * @param {number} x
 * @param {number} y
 * @returns {number} índice, ou -1 se não houver retângulo nenhum
 */
export function indiceMaisProximo(retangulos, x, y) {
  if (!Array.isArray(retangulos) || retangulos.length === 0) return -1;

  let melhor = -1;
  let menorDistancia = Infinity;

  retangulos.forEach((retangulo, indice) => {
    if (!retangulo) return;
    const centroX = retangulo.left + retangulo.width / 2;
    const centroY = retangulo.top + retangulo.height / 2;
    // Sem `Math.sqrt`: comparar quadrados dá a mesma ordem e poupa a raiz num
    // laço que roda a cada movimento do ponteiro.
    const distancia = (centroX - x) ** 2 + (centroY - y) ** 2;
    if (distancia < menorDistancia) {
      menorDistancia = distancia;
      melhor = indice;
    }
  });

  return melhor;
}

/**
 * Reescreve o campo `order` de cada item para bater com a posição na lista.
 *
 * O backend ordena os itens da trilha por esta coluna (`activity_items.order`),
 * e é ela que vira a sequência de fases do aluno. A posição no array só existe
 * enquanto a tela está aberta.
 *
 * Começa em 1, e não em 0: é o número que o professor vê no selo do card e o que
 * o aluno vê como "Fase 1".
 *
 * @param {Array} lista
 * @returns {Array} lista nova, com `order` coerente
 */
export function renumerar(lista) {
  if (!Array.isArray(lista)) return [];
  return lista.map((item, indice) => ({ ...item, order: indice + 1 }));
}

/**
 * Move um passo uma casa para a esquerda ou para a direita.
 *
 * É o caminho do teclado e o dos botões — e existir não é gentileza: arrasto é o
 * único gesto que não tem como ser feito por quem navega por teclado, e num
 * toque impreciso ele é o que falha primeiro.
 *
 * Nas pontas não faz nada, de propósito: circular faria o primeiro item saltar
 * para o fim da trilha com um toque, que é o oposto do que quem aperta "esquerda"
 * está pedindo.
 *
 * @param {Array} lista
 * @param {number} indice
 * @param {number} direcao -1 (esquerda) ou 1 (direita)
 */
export function moverUmaCasa(lista, indice, direcao) {
  return mover(lista, indice, indice + direcao);
}

/**
 * Quanto rolar por quadro quando o ponteiro chega perto da borda da área.
 *
 * A grade tem altura máxima e rola por dentro, então numa trilha longa o destino
 * do arrasto costuma estar fora da vista. Sem isto, o professor teria de soltar o
 * card, rolar, e pegar de novo — e como a ordem já mudou no primeiro passo, ele
 * teria de refazer a conta de onde o passo está.
 *
 * A velocidade é proporcional a quanto o ponteiro AVANÇOU dentro da zona: encostar
 * na beirada rola devagar, ir até o limite rola rápido. Um valor fixo obriga a
 * escolher entre lento demais para atravessar 40 passos e rápido demais para parar
 * no passo certo.
 *
 * Devolve pixels por quadro: negativo sobe, positivo desce, zero fica parado.
 *
 * @param {number} doTopo distância do ponteiro à borda de cima da área
 * @param {number} doFundo distância do ponteiro à borda de baixo
 * @param {number} zona espessura da faixa sensível, em px
 * @param {number} maxima velocidade no limite da zona, em px por quadro
 */
export function velocidadeDaRolagem(doTopo, doFundo, zona, maxima) {
  if (!(zona > 0)) return 0;

  // O lado mais próximo ganha. Empate (área menor que duas zonas) fica parado, em
  // vez de tremer entre subir e descer.
  if (doTopo < doFundo && doTopo < zona) {
    return -Math.ceil(((zona - Math.max(doTopo, 0)) / zona) * maxima);
  }
  if (doFundo < doTopo && doFundo < zona) {
    return Math.ceil(((zona - Math.max(doFundo, 0)) / zona) * maxima);
  }
  return 0;
}

function ehIndiceValido(lista, indice) {
  return Number.isInteger(indice) && indice >= 0 && indice < lista.length;
}
